import type { CSSObject } from '../theme/themeTypes'
import React from 'react'
import { styled } from '../../utils/styled'

export const CopilotIcon = React.forwardRef<SVGSVGElement, { isLoading?: boolean } & React.SVGProps<SVGSVGElement>>(
  (props, ref): React.ReactElement => {
    const { isLoading = false, ...rest } = props
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...rest}
        ref={ref}
      >
        {/* SPINNING GROUP:
      Contains the two stroke paths (the curved lines).
      When isLoading is true, these will rotate.
    */}
        <SpinningGroup className={isLoading ? 'loading' : ''}>
          <path d="M12 20C7.58172 20 4 16.4183 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path
            d="M17.5001 6.1905C16.0664 4.83275 14.1305 4 12.0001 4C10.4326 4 8.9704 4.4508 7.73608 5.22983"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </SpinningGroup>

        {/* FADE GROUP:
      Contains the two smaller stars (paths 3 and 4).
      When isLoading is true, these will disappear (opacity: 0).
    */}
        <FadeGroup className={isLoading ? 'loading' : ''}>
          <path
            d="M15.409 18.1597C15.5389 17.9468 15.8481 17.9468 15.978 18.1597L16.4095 18.8666C16.4371 18.9119 16.4751 18.9499 16.5204 18.9775L17.2273 19.409C17.4402 19.5389 17.4402 19.8481 17.2273 19.978L16.5204 20.4095C16.4751 20.4371 16.4371 20.4751 16.4095 20.5204L15.978 21.2273C15.8481 21.4402 15.5389 21.4402 15.409 21.2273L14.9775 20.5204C14.9499 20.4751 14.9119 20.4371 14.8666 20.4095L14.1597 19.978C13.9468 19.8481 13.9468 19.5389 14.1597 19.409L14.8666 18.9775C14.9119 18.9499 14.9499 18.9119 14.9775 18.8666L15.409 18.1597Z"
            fill="currentColor"
          />
          <path
            d="M4.40896 5.15968C4.53891 4.94677 4.84807 4.94677 4.97801 5.15968L5.40948 5.86662C5.4371 5.91187 5.47512 5.94988 5.52035 5.9775L6.2273 6.40897C6.44021 6.53891 6.44021 6.84808 6.2273 6.97802L5.52035 7.40949C5.47512 7.43711 5.4371 7.47511 5.40948 7.52036L4.97801 8.22731C4.84807 8.44021 4.53891 8.44021 4.40896 8.22731L3.97749 7.52036C3.94987 7.47511 3.91186 7.43711 3.86662 7.40949L3.15968 6.97802C2.94678 6.84808 2.94678 6.53891 3.15968 6.40897L3.86662 5.9775C3.91186 5.94988 3.94987 5.91187 3.97749 5.86662L4.40896 5.15968Z"
            fill="currentColor"
          />
        </FadeGroup>

        {/* PULSATING GROUP:
      Contains the large central star.
      It always pulses gently, regardless of loading state (based on your video).
    */}
        <PulsatingGroup>
          <path
            d="M11.5344 7.18873C11.7003 6.76518 12.2997 6.76518 12.4656 7.18873L13.6089 10.1079C13.6597 10.2377 13.7623 10.3403 13.8921 10.3911L16.8113 11.5344C17.2348 11.7003 17.2348 12.2997 16.8113 12.4656L13.8921 13.6089C13.7623 13.6597 13.6597 13.7623 13.6089 13.8921L12.4656 16.8113C12.2997 17.2348 11.7003 17.2348 11.5344 16.8113L10.3911 13.8921C10.3403 13.7623 10.2377 13.6597 10.1079 13.6089L7.18873 12.4656C6.76518 12.2997 6.76518 11.7003 7.18873 11.5344L10.1079 10.3911C10.2377 10.3403 10.3403 10.2377 10.3911 10.1079L11.5344 7.18873Z"
            fill="currentColor"
          />
        </PulsatingGroup>
      </svg>
    )
  }
)

// CSS IMPLEMENTATION

const SpinningGroup = styled('g')(
  (): CSSObject => ({
    // Use transform-box to ensure rotation happens around the element's center correctly in SVG
    transformOrigin: '12px 12px',
    transition: 'opacity 0.3s ease',

    '@keyframes spin': {
      from: {
        transform: 'rotate(0deg)'
      },
      to: {
        transform: 'rotate(360deg)'
      }
    },
    '&.loading': {
      animation: 'spin 1s cubic-bezier(0.65, 0, 0.35, 1) infinite' // Linear timing matches the video best
    }
  })
)

const PulsatingGroup = styled('g')(
  (): CSSObject => ({
    transformBox: 'fill-box',
    transformOrigin: 'center',

    '@keyframes pulsate': {
      '0%': {
        transform: 'scale(1)'
      },
      '50%': {
        transform: 'scale(0.85)' // Subtle pulse
      },
      '100%': {
        transform: 'scale(1)'
      }
    },
    // The central star usually pulses gently even when not loading
    animation: 'pulsate 2s ease-in-out infinite'
  })
)

const FadeGroup = styled('g')(
  (): CSSObject => ({
    opacity: 1,
    transition: 'opacity 0.2s ease-in-out',
    '&.loading': {
      opacity: 0 // Hides the small stars instantly when loading starts
    }
  })
)

export default CopilotIcon
